/**
 * "What could go wrong" for a downsizing plan (downsizing rebuild, 21 Oct 2026; the agreed mock-up's d-risks).
 *
 * The shared list (strategyRisks.ts) with the points that are about THIS plan put right:
 *   - the stamp duty on the new home is said as the new home's ("recovered only if the new home grows by at
 *     least that much"), not a property bought with the proceeds;
 *   - "the money is no longer exempt … so the pension can fall" is said only where the plan frees money that
 *     is counted. A plan that puts the whole sale into the new home frees nothing (review defect: it was said
 *     on a plan whose pension rises by $10,252);
 *   - buying and selling rarely line up: the 24-month exemption, and that a gap means bridging or renting.
 */
import { buildStrategyRisks, riskKindsFromCandidateId, type RiskKind, type StrategyRisks } from '../../strategy/strategyRisks.ts';
import type { DzMoveRecord } from './dzChosenPlan.ts';

const money = (n: number): string => `$${Math.round(n).toLocaleString('en-AU')}`;

export interface DzRiskInput {
    kinds: readonly RiskKind[];
    move: (Pick<DzMoveRecord, 'stampDuty' | 'freedCapital' | 'sellAndRent' | 'newHomePrice'> & { buyUpShortfall?: number }) | null;
    /** The duty on an investment property the plan buys, if any. */
    ipStampDuty?: number | null;
    horizonWarning?: string | null;
}

export function dzPlanRisks({ kinds, move, ipStampDuty, horizonWarning }: DzRiskInput): StrategyRisks {
    const base = buildStrategyRisks(kinds, { horizonWarning: horizonWarning ?? null });
    const forYou = [...base.forYou];
    if (move && !move.sellAndRent && move.stampDuty > 0) {
        forYou.push({ icon: 'receipt', lead: `${money(move.stampDuty)} of stamp duty is paid on day one.`, body: 'It is recovered only if the new home grows by at least that much.' });
    }
    // A dearer home is paid partly from savings: that money becomes part of the home, hard to reach if costs rise
    // (live review, 22 Oct 2026 — the plan moved $476,847 into the house and nothing said so).
    if (move && !move.sellAndRent && (move.buyUpShortfall ?? 0) >= 1) {
        forYou.push({ icon: 'lock', lead: `${money(move.buyUpShortfall ?? 0)} of savings goes into the home.`, body: 'It is no longer money to draw on: if costs rise or care is needed, it comes back only by selling or borrowing against the home.' });
    }
    if (ipStampDuty != null && ipStampDuty > 0) {
        forYou.push({ icon: 'receipt', lead: `${money(ipStampDuty)} of stamp duty on the investment property.`, body: 'It comes out of the money the sale frees, and is only recovered if the property grows by at least that much.' });
    }
    const freesNothing = !!move && !move.sellAndRent && move.freedCapital < 1;
    const sections = base.sections.map((s) => {
        if (s.kind !== 'sellHome' || !move) return s;
        const points = s.points.filter((p) => !(freesNothing && p.icon === 'landmark'));
        if (!move.sellAndRent && move.newHomePrice > 0) {
            points.push({ icon: 'clock', lead: "Buying and selling don't line up.", body: 'Money set aside for the new home is exempt for up to 24 months, but a gap means bridging or renting.' });
        }
        return { ...s, points };
    });
    return { forYou, sections };
}

/** The parts of a plan from its id and what it actually does (a hand-built plan has no engine id). */
export function dzRiskKindsOf(id: string, inputs: Record<string, unknown> | null | undefined): RiskKind[] {
    const kinds: RiskKind[] = [...riskKindsFromCandidateId(id)];
    const add = (k: RiskKind) => { if (!kinds.includes(k)) kinds.push(k); };
    const d = inputs || {};
    const dep = (d.deployment || {}) as Record<string, unknown>;
    const n = (v: unknown) => Number(v) || 0;
    if (n(d.salePrice) > 0) add('sellHome');
    if (d.rentInstead === true) add('sellAndRent');
    if (n(d.downsizerContribution) + n(d.partnerDownsizerContribution) + n(dep.superContribution) + n(dep.partnerSuperContribution) > 0) add('superContribution');
    if (d.commenceIncomeStream === true || dep.superCommenceIncomeStream === true) add('accountBasedPension');
    if (n(dep.annuity) > 0 && !kinds.some((k) => k.startsWith('annuity'))) add(dep.annuityType === 'guaranteed' ? 'annuityGuaranteed' : n(dep.annuityResidualPercent) > 0 ? 'annuityReturned' : 'annuityNil');
    if (n(dep.investmentProperty) > 0) add('investmentProperty');
    if (n(dep.shares) > 0) add('shares');
    if (n(dep.investmentBond) > 0) add('investmentBond');
    if (n(d.funeralBondPurchase) > 0) add('funeralBond');
    if (n(d.giftingAmount) > 0) add('gift');
    return kinds.filter((k) => k !== 'stayPut');
}
